import { OrderStatus } from "../../components/order/OrderConstants";
import { DocumentReferenceViewModel } from "../../model/DocumentReferenceViewModel";
import { AssignCandidatesRequestModel } from "../../model/examSessionCandidate/AssignCandidatesRequestModel";
import { ExamSessionCandidateViewModel } from "../../model/examSessionCandidate/ExamSessionCandidateViewModel";
import { ReBookCandidateExamRequestModel } from "../../model/examSessionCandidate/ReBookCandidateExamRequestModel";
import { UpdateCandidateModuleRequestModel } from "../../model/examSessionCandidate/UpdateCandidateModuleRequestModel";
import { ApiResponse } from "../CustomFetch";
import { api, transformApiResponse } from "./BaseApi";

const apiUrl = "/examSessionCandidate";

const getExamSessionCandidateUrl = (id?: string) => (id === undefined ? apiUrl : `${apiUrl}/${id}`);

const examSessionCandidateApi = api
  .enhanceEndpoints({ addTagTypes: ["ExamSessionCandidates", "AvailableDays", "MyCandidateExams"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getExamSessionCandidates: builder.query<ExamSessionCandidateViewModel[], string>({
        query: (examSessionId) => `/examSession/${examSessionId}/examSessionCandidates`,
        transformResponse: (response: ApiResponse<ExamSessionCandidateViewModel[]>) => response.data,
        providesTags: (result) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: "ExamSessionCandidates" as const, id })),
                { type: "ExamSessionCandidates", id: "LIST" },
              ]
            : [{ type: "ExamSessionCandidates", id: "LIST" }],
      }),
      getExamSessionCandidateById: builder.query<ExamSessionCandidateViewModel, string>({
        query: getExamSessionCandidateUrl,
        transformResponse: (response: ApiResponse<ExamSessionCandidateViewModel>) => response.data,
        providesTags: (_result, _error, arg) => [{ type: "ExamSessionCandidates", id: arg }],
      }),
      cancelCandidateModule: builder.mutation<ApiResponse<OrderStatus>, { examSessionCandidateId: string; moduleId: string; comments?: string  }>({
        query: ({ examSessionCandidateId, moduleId, comments }) => ({
          url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/cancel`,
          method: "POST",
          body: { comments },
          transformResponse: transformApiResponse,
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: "ExamSessionCandidates", id: arg.examSessionCandidateId },
          "AvailableDays",
          "MyCandidateExams",
        ],
      }),
      reBookCandidateExam: builder.mutation<
        ApiResponse<void>,
        { examSessionCandidateId: string; moduleId: string; examId: string; data?: ReBookCandidateExamRequestModel }
      >({
        query: ({ examSessionCandidateId, moduleId, examId, data }) => ({
          url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/exam/${examId}/reBook`,
          body: data === undefined ? {} : data,
          method: "POST",
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: "ExamSessionCandidates", id: arg.examSessionCandidateId },
          "AvailableDays",
        ],
      }),
      assignCandidatesAndExperts: builder.mutation<ApiResponse<void>, AssignCandidatesRequestModel>({
        query: (data) => ({
          url: `${getExamSessionCandidateUrl()}/assign`,
          body: data,
          method: "POST",
        }),
        invalidatesTags: (_result, _error, arg) => {
          var candidateTags = arg.examSessionCandidates.map((candidate) => ({
            type: "ExamSessionCandidates" as const,
            id: candidate.examSessionCandidateId,
          }));
          return [...candidateTags, "AvailableDays"];
        },
      }),
      updateCandidateModule: builder.mutation<
        ApiResponse<void>,
        { examSessionCandidateId: string; moduleId: string; data: UpdateCandidateModuleRequestModel }
      >({
        query: ({ examSessionCandidateId, moduleId, data }) => ({
          url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}`,
          body: data,
          method: "PATCH",
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: "ExamSessionCandidates", id: arg.examSessionCandidateId }],
      }),
      approveCandidateModule: builder.mutation<ApiResponse<void>, { examSessionCandidateId: string; moduleId: string }>(
        {
          query: ({ examSessionCandidateId, moduleId }) => ({
            url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/approve`,
            body: {},
            method: "POST",
          }),
          invalidatesTags: (_result, _error, arg) => [
            { type: "ExamSessionCandidates", id: arg.examSessionCandidateId },
            "AvailableDays",
          ],
        }
      ),
      rejectCandidateModule: builder.mutation<ApiResponse<void>, { examSessionCandidateId: string; moduleId: string }>({
        query: ({ examSessionCandidateId, moduleId }) => ({
          url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/reject`,
          body: {},
          method: "POST",
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: "ExamSessionCandidates", id: arg.examSessionCandidateId },
          "AvailableDays",
        ],
      }),
      listModuleDocuments: builder.query<
        DocumentReferenceViewModel[],
        { examSessionCandidateId: string; moduleId: string }
      >({
        query: ({ examSessionCandidateId, moduleId }) =>
          `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/documents`,
        transformResponse: transformApiResponse,
      }),
      rebookCandidateModule: builder.mutation<ApiResponse<void>, { examSessionCandidateId: string; moduleId: string, rebookWithInvoice: boolean }>({
        query: ({ examSessionCandidateId, moduleId, rebookWithInvoice }) => ({
          url: `${getExamSessionCandidateUrl(examSessionCandidateId)}/module/${moduleId}/rebook/${rebookWithInvoice}`,
          body: {},
          method: "POST",
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: "ExamSessionCandidates", id: arg.examSessionCandidateId },
          "AvailableDays",
        ],
      }),

      calculateExamResults: builder.mutation<ApiResponse<void>, string>({
        query: (examSessionId) => ({
          url: `${apiUrl}/examSession/${examSessionId}/calculateExamResults`,
          body: {},
          method: "POST",
        }),
        invalidatesTags: () => [{ type: "ExamSessionCandidates" }],
      }),
    }),
  });

export const {
  useGetExamSessionCandidatesQuery,
  useGetExamSessionCandidateByIdQuery,
  useReBookCandidateExamMutation,
  useAssignCandidatesAndExpertsMutation,
  useUpdateCandidateModuleMutation,
  useApproveCandidateModuleMutation,
  useRejectCandidateModuleMutation,
  useListModuleDocumentsQuery,
  useCalculateExamResultsMutation,
  useCancelCandidateModuleMutation,
  useRebookCandidateModuleMutation,
} = examSessionCandidateApi;
